import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../../../config/AuthContext';
import {
    Card,
    Button,
    Space,
    Typography,
    Tooltip,
    Input,
    Tag,
    Modal,
    message,
    Alert,
    Table,
    Tabs
} from 'antd';
import {
    DownloadOutlined,
    UploadOutlined,
    CheckCircleOutlined,
    CloseCircleOutlined,
    ArrowLeftOutlined,
    SaveOutlined,
    PlusOutlined,
    DeleteOutlined,
    EditOutlined,
    CodeOutlined,
    EyeOutlined
} from '@ant-design/icons';
import { ScriptRelationalData, ScriptTable, ScriptAttribute } from './model/ErModel';
import {
    parseScriptTextToData,
    formatDataToScriptText,
    validateScriptRelational,
    ScriptValidationResult
} from './engine/ScriptRelationalEngine';

const { Text, Title } = Typography;
const { TextArea } = Input;

interface ScriptRelationalIdeProps {
    initialData?: ScriptRelationalData | string | null;
    solutionData?: ScriptRelationalData | string | null;
    readOnly?: boolean;
    isTeacherMode?: boolean;
    hideImportExport?: boolean;
    hideSave?: boolean;
    hideEvaluate?: boolean;
    onSave?: (data: ScriptRelationalData, formattedText: string) => void;
    onManualSave?: (data: ScriptRelationalData, formattedText: string) => void | Promise<void>;
    onSubmitEvaluation?: (data: ScriptRelationalData, formattedText: string, result: ScriptValidationResult) => void;
    customConfig?: any;
    intentsFallitsPrevis?: number;
    title?: string;
    onBack?: () => void;
}

export const ScriptRelationalIde: React.FC<ScriptRelationalIdeProps> = ({
    initialData,
    solutionData,
    readOnly = false,
    isTeacherMode = false,
    hideImportExport = false,
    hideSave = false,
    hideEvaluate = false,
    onSave,
    onManualSave,
    onSubmitEvaluation,
    customConfig,
    intentsFallitsPrevis = 0,
    title = 'IDE de Disseny: Script Relacional',
    onBack
}) => {
    // Estat textual i estructurat
    const [rawText, setRawText] = useState<string>(() => {
        if (!initialData) return '';
        if (typeof initialData === 'string') {
            try {
                const parsed = JSON.parse(initialData);
                if (parsed && parsed.type === 'Script') {
                    return formatDataToScriptText(parsed);
                }
            } catch (e) { }
            return initialData;
        }
        return formatDataToScriptText(initialData);
    });

    const [scriptData, setScriptData] = useState<ScriptRelationalData>(() => {
        if (!initialData) return { type: 'Script', version: 1, tables: [] };
        if (typeof initialData === 'string') {
            try {
                const parsed = JSON.parse(initialData);
                if (parsed && parsed.type === 'Script') return parsed;
            } catch (e) { }
            return parseScriptTextToData(initialData);
        }
        return initialData;
    });

    // Sessió d'usuari per determinar permisos
    const { user } = useContext(AuthContext);
    const isTeacherOrAdmin = user?.tipus === '0' || user?.tipus === '1' || isTeacherMode;

    const [activeTab, setActiveTab] = useState<'visual' | 'text'>('visual');
    const [evalResult, setEvalResult] = useState<ScriptValidationResult | null>(null);
    const [evalModalVisible, setEvalModalVisible] = useState(false);
    const [newTableModalVisible, setNewTableModalVisible] = useState(false);
    const [newTableName, setNewTableName] = useState('');
    const [saving, setSaving] = useState(false);

    // Sincronitzar quan arriba o canvia initialData (per exemple, en carregar dades d'enviament de l'alumne)
    useEffect(() => {
        if (!initialData) return;
        if (typeof initialData === 'string') {
            try {
                const parsed = JSON.parse(initialData);
                if (parsed && (parsed.type === 'Script' || Array.isArray(parsed.tables))) {
                    setScriptData(parsed);
                    setRawText(formatDataToScriptText(parsed));
                    return;
                }
            } catch (e) { }
            setRawText(initialData);
            setScriptData(parseScriptTextToData(initialData));
        } else if (initialData && ((initialData as any).type === 'Script' || Array.isArray((initialData as any).tables))) {
            setScriptData(initialData);
            setRawText(formatDataToScriptText(initialData));
        }
    }, [initialData]);

    // Actualitzar text i JSON sincrònicament
    const updateFromText = (text: string) => {
        setRawText(text);
        const parsed = parseScriptTextToData(text);
        setScriptData(parsed);
        onSave?.(parsed, text);
    };

    const updateFromData = (data: ScriptRelationalData) => {
        setScriptData(data);
        const formatted = formatDataToScriptText(data);
        setRawText(formatted);
        onSave?.(data, formatted);
    };

    // Toggle PK d'un atribut
    const togglePk = (tableIdx: number, attrIdx: number) => {
        if (readOnly) return;
        const next = { ...scriptData, tables: [...scriptData.tables] };
        const targetTable = { ...next.tables[tableIdx], attributes: [...next.tables[tableIdx].attributes] };
        targetTable.attributes[attrIdx] = {
            ...targetTable.attributes[attrIdx],
            isPK: !targetTable.attributes[attrIdx].isPK
        };
        next.tables[tableIdx] = targetTable;
        updateFromData(next);
    };

    // Canviar referència FK d'un atribut
    const setFkTarget = (tableIdx: number, attrIdx: number, targetTable: string | null) => {
        if (readOnly) return;
        const next = { ...scriptData, tables: [...scriptData.tables] };
        const t = { ...next.tables[tableIdx], attributes: [...next.tables[tableIdx].attributes] };
        t.attributes[attrIdx] = {
            ...t.attributes[attrIdx],
            fkTargetTable: targetTable && targetTable.trim() ? targetTable.trim() : null
        };
        next.tables[tableIdx] = t;
        updateFromData(next);
    };

    // Afegir atribut a una taula
    const addAttribute = (tableIdx: number) => {
        if (readOnly) return;
        const next = { ...scriptData, tables: [...scriptData.tables] };
        const t = { ...next.tables[tableIdx], attributes: [...next.tables[tableIdx].attributes] };
        t.attributes.push({
            name: `atr_${t.attributes.length + 1}`,
            isPK: false,
            fkTargetTable: null
        });
        next.tables[tableIdx] = t;
        updateFromData(next);
    };

    // Eliminar atribut
    const removeAttribute = (tableIdx: number, attrIdx: number) => {
        if (readOnly) return;
        const next = { ...scriptData, tables: [...scriptData.tables] };
        const t = { ...next.tables[tableIdx], attributes: [...next.tables[tableIdx].attributes] };
        t.attributes.splice(attrIdx, 1);
        next.tables[tableIdx] = t;
        updateFromData(next);
    };

    // Afegir nova taula (inicialment buida, sense atributs ni PK per defecte)
    const handleAddTable = () => {
        if (!newTableName.trim()) return;
        const next: ScriptRelationalData = {
            ...scriptData,
            tables: [
                ...scriptData.tables,
                {
                    name: newTableName.trim().toUpperCase(),
                    attributes: []
                }
            ]
        };
        updateFromData(next);
        setNewTableName('');
        setNewTableModalVisible(false);
    };

    // Eliminar taula
    const removeTable = (tableIdx: number) => {
        if (readOnly) return;
        const next = {
            ...scriptData,
            tables: scriptData.tables.filter((_, idx) => idx !== tableIdx)
        };
        updateFromData(next);
    };

    // Avaluar contra la solució
    const handleEvaluate = () => {
        let sol: ScriptRelationalData | null = null;
        if (typeof solutionData === 'string') {
            try {
                sol = JSON.parse(solutionData);
            } catch (e) {
                sol = parseScriptTextToData(solutionData);
            }
        } else if (solutionData) {
            sol = solutionData;
        }

        if (!sol || !sol.tables || sol.tables.length === 0) {
            message.warning("No hi ha una solució vàlida configurada pel professor.");
            return;
        }

        const res = validateScriptRelational(scriptData, sol, customConfig, intentsFallitsPrevis);
        setEvalResult(res);
        setEvalModalVisible(true);

        if (onSubmitEvaluation) {
            onSubmitEvaluation(scriptData, rawText, res);
        }
    };

    // Exportar JSON
    const handleExportJson = () => {
        const blob = new Blob([JSON.stringify(scriptData, null, 2)], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `script_relacional_${Date.now()}.json`;
        a.click();
        URL.revokeObjectURL(url);
        message.success('JSON exportat!');
    };

    // Importar JSON
    const handleImportJson = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (evt) => {
            try {
                const text = evt.target?.result as string;
                if (text.trim().startsWith('{')) {
                    const parsed = JSON.parse(text);
                    if (parsed.tables) {
                        updateFromData(parsed);
                        message.success('Script JSON importat!');
                        return;
                    }
                }
                // Si és text pla tipus SCRIPT
                updateFromText(text);
                message.success('Script importat!');
            } catch (err) {
                message.error('Error al carregar el fitxer');
            }
        };
        reader.readAsText(file);
    };

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%', background: '#f5f7fa', overflow: 'hidden' }}>
            {/* Header */}
            <div style={{
                background: '#1f2430',
                padding: '8px 16px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                color: '#fff',
                borderBottom: '1px solid #333'
            }}>
                <Space size="middle">
                    {onBack && (
                        <Button
                            type="text"
                            icon={<ArrowLeftOutlined style={{ color: '#fff' }} />}
                            onClick={onBack}
                            style={{ color: '#fff' }}
                        >
                            Tornar
                        </Button>
                    )}
                    <span style={{ fontWeight: 700, fontSize: 15, color: '#13c2c2' }}>
                        {title}
                    </span>
                    {isTeacherMode && <Tag color="gold">Mode Solució Professor</Tag>}
                </Space>

                <Space size="small">
                    {isTeacherOrAdmin && !hideImportExport && (
                        <>
                            <Tooltip title="Exportar dades a JSON">
                                <Button size="small" icon={<DownloadOutlined />} onClick={handleExportJson}>
                                    Exportar JSON
                                </Button>
                            </Tooltip>
                            <label style={{ margin: 0 }}>
                                <Tooltip title="Importar fitxer JSON o text">
                                    <span className="ant-btn ant-btn-sm ant-btn-default" style={{ display: 'inline-flex', alignItems: 'center', cursor: 'pointer' }}>
                                        <UploadOutlined style={{ marginRight: 6 }} /> Importar
                                    </span>
                                </Tooltip>
                                <input type="file" style={{ display: 'none' }} accept=".json,.txt" onChange={handleImportJson} />
                            </label>
                        </>
                    )}

                    {!readOnly && !hideSave && (
                        <Button
                            type="primary"
                            icon={<SaveOutlined />}
                            size="small"
                            loading={saving}
                            onClick={async () => {
                                setSaving(true);
                                try {
                                    if (onManualSave) {
                                        await onManualSave(scriptData, rawText);
                                    } else {
                                        onSave?.(scriptData, rawText);
                                        message.success('Script Relacional desat!');
                                    }
                                } catch (e: any) {
                                    message.error('Error en desar: ' + (e?.message || e));
                                } finally {
                                    setSaving(false);
                                }
                            }}
                        >
                            Desar
                        </Button>
                    )}

                    {!hideEvaluate && !isTeacherMode && solutionData && (
                        <Button
                            type="primary"
                            style={{ background: '#52c41a', borderColor: '#52c41a', fontWeight: 600 }}
                            size="small"
                            icon={<CheckCircleOutlined />}
                            onClick={handleEvaluate}
                        >
                            Avaluar & Validar
                        </Button>
                    )}
                </Space>
            </div>

            {/* Selector de mode (Targetes Visuals interactives vs Codi Textual per a professors) */}
            <div style={{ background: '#fff', borderBottom: '1px solid #e8e8e8', padding: '0 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                {isTeacherOrAdmin ? (
                    <Tabs
                        activeKey={activeTab}
                        onChange={(k: any) => setActiveTab(k)}
                        style={{ marginBottom: 0 }}
                        items={[
                            { key: 'visual', label: <span><EyeOutlined /> Editor Visual de Taules</span> },
                            { key: 'text', label: <span><CodeOutlined /> Editor Textual (Sintaxi línies)</span> }
                        ]}
                    />
                ) : (
                    <div style={{ padding: '10px 0', fontWeight: 600, color: '#08979c', display: 'flex', alignItems: 'center', gap: 6 }}>
                        <EyeOutlined /> Editor Visual de Taules i Esquemes
                    </div>
                )}

                {activeTab === 'visual' && !readOnly && (
                    <Button
                        type="primary"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() => setNewTableModalVisible(true)}
                        style={{ background: '#13c2c2', borderColor: '#13c2c2' }}
                    >
                        Nova Taula
                    </Button>
                )}
            </div>

            {/* Contingut Principal */}
            <div style={{ flex: 1, padding: 16, overflowY: 'auto' }}>
                {activeTab === 'text' ? (
                    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                        <div style={{ marginBottom: 8, color: '#666', fontSize: 13 }}>
                            Escriu una taula per línia en format: <code>NOM_TAULA (*id_pk*, camp1, camp2, camp_fk (TAULA_DESTI))</code>. Els asteriscs marquen la <strong>Clau Primària (PK)</strong>.
                        </div>
                        <TextArea
                            rows={16}
                            value={rawText}
                            readOnly={readOnly}
                            onChange={e => updateFromText(e.target.value)}
                            placeholder="EQUIPO (*nombre_eq*, dirección, telefono, presidente)&#10;ESTADIO (*nombre_est*, dirección_est, capacidad, nombre_eq (EQUIPO))"
                            style={{
                                fontFamily: 'monospace',
                                fontSize: 14,
                                flex: 1,
                                borderRadius: 6
                            }}
                        />
                    </div>
                ) : (
                    <div>
                        {scriptData.tables.length === 0 ? (
                            <div style={{ textAlign: 'center', padding: 48, color: '#999' }}>
                                Sense taules definides encara. Clica a "Nova Taula" o utilitza l'editor textual per començar.
                            </div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                                {scriptData.tables.map((table, tIdx) => (
                                    <Card
                                        key={tIdx}
                                        size="small"
                                        style={{
                                            borderRadius: 8,
                                            boxShadow: '0 2px 6px rgba(0,0,0,0.03)',
                                            border: '1px solid #d9d9d9'
                                        }}
                                        title={
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                                <Space>
                                                    <Tag color="cyan" style={{ fontSize: 14, fontWeight: 'bold', padding: '2px 8px' }}>
                                                        {table.name}
                                                    </Tag>
                                                    <span style={{ fontSize: 12, color: '#888' }}>
                                                        ({table.attributes.length} atributs)
                                                    </span>
                                                </Space>
                                                {!readOnly && (
                                                    <Space size="small">
                                                        <Button
                                                            size="small"
                                                            icon={<PlusOutlined />}
                                                            onClick={() => addAttribute(tIdx)}
                                                        >
                                                            Afegir atribut
                                                        </Button>
                                                        <Button
                                                            size="small"
                                                            danger
                                                            type="text"
                                                            icon={<DeleteOutlined />}
                                                            onClick={() => removeTable(tIdx)}
                                                        />
                                                    </Space>
                                                )}
                                            </div>
                                        }
                                    >
                                        {/* Renderitzat de la signatura estil acadèmic */}
                                        <div style={{
                                            background: '#fafafa',
                                            padding: '8px 12px',
                                            borderRadius: 6,
                                            marginBottom: 12,
                                            fontFamily: 'monospace',
                                            fontSize: 14,
                                            border: '1px solid #f0f0f0'
                                        }}>
                                            <span style={{ fontWeight: 'bold', color: '#006d75' }}>{table.name}</span> (
                                            {table.attributes.map((attr, aIdx) => (
                                                <span key={aIdx} style={{ marginRight: 6 }}>
                                                    <span style={{
                                                        textDecoration: attr.isPK ? 'underline' : 'none',
                                                        fontWeight: attr.isPK ? 'bold' : 'normal',
                                                        color: attr.isPK ? '#cf1322' : '#262626'
                                                    }}>
                                                        {attr.name}
                                                    </span>
                                                    {attr.fkTargetTable && (
                                                        <span style={{ color: '#096dd9', marginLeft: 4, fontWeight: 600 }}>
                                                            ({attr.fkTargetTable})
                                                        </span>
                                                    )}
                                                    {aIdx < table.attributes.length - 1 && ','}
                                                </span>
                                            ))}
                                            )
                                        </div>

                                        {/* Controls interactius d'atributs */}
                                        {!readOnly && (
                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                                                {table.attributes.map((attr, aIdx) => (
                                                    <Card
                                                        key={aIdx}
                                                        size="small"
                                                        style={{
                                                            width: 260,
                                                            background: attr.isPK ? '#fff1f0' : (attr.fkTargetTable ? '#e6f7ff' : '#ffffff'),
                                                            border: attr.isPK ? '1px solid #ffa39e' : (attr.fkTargetTable ? '1px solid #91d5ff' : '1px solid #f0f0f0')
                                                        }}
                                                    >
                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
                                                            <Input
                                                                size="small"
                                                                value={attr.name}
                                                                style={{ width: 130, fontWeight: attr.isPK ? 'bold' : 'normal' }}
                                                                onChange={e => {
                                                                    const next = { ...scriptData };
                                                                    next.tables[tIdx].attributes[aIdx].name = e.target.value;
                                                                    updateFromData(next);
                                                                }}
                                                            />
                                                            <Button
                                                                size="small"
                                                                type="text"
                                                                danger
                                                                icon={<DeleteOutlined />}
                                                                onClick={() => removeAttribute(tIdx, aIdx)}
                                                            />
                                                        </div>

                                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 4 }}>
                                                            <Button
                                                                size="small"
                                                                type={attr.isPK ? 'primary' : 'default'}
                                                                danger={attr.isPK}
                                                                onClick={() => togglePk(tIdx, aIdx)}
                                                                style={{ fontSize: 11, padding: '0 6px', height: 22 }}
                                                            >
                                                                {attr.isPK ? '✓ Clau (PK)' : 'Marcar PK'}
                                                            </Button>

                                                            <Input
                                                                size="small"
                                                                placeholder="FK (Destí)"
                                                                value={attr.fkTargetTable || ''}
                                                                style={{ width: 110, fontSize: 11 }}
                                                                onChange={e => setFkTarget(tIdx, aIdx, e.target.value)}
                                                            />
                                                        </div>
                                                    </Card>
                                                ))}
                                            </div>
                                        )}
                                    </Card>
                                ))}
                            </div>
                        )}
                    </div>
                )}
            </div>

            {/* Modal Nova Taula */}
            <Modal
                title="Afegir Taula al Script Relacional"
                open={newTableModalVisible}
                onCancel={() => setNewTableModalVisible(false)}
                onOk={handleAddTable}
                okText="Crear Taula"
                cancelText="Cancel·lar"
            >
                <div style={{ marginBottom: 8 }}>Nom de la Taula:</div>
                <Input
                    placeholder="Ex: JUGADOR, FACTURA, LINIA_COMANDA"
                    value={newTableName}
                    onChange={e => setNewTableName(e.target.value)}
                    onPressEnter={handleAddTable}
                    autoFocus
                />
            </Modal>

            {/* Modal Avaluació */}
            <Modal
                title={
                    <Space>
                        {evalResult?.isCorrect ? (
                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: 22 }} />
                        ) : (
                            <CloseCircleOutlined style={{ color: '#f5222d', fontSize: 22 }} />
                        )}
                        <span style={{ fontWeight: 700 }}>
                            {evalResult?.isCorrect ? 'Script Relacional Correcte!' : 'Correcció del Script Relacional'}
                        </span>
                    </Space>
                }
                open={evalModalVisible}
                onCancel={() => setEvalModalVisible(false)}
                footer={[<Button key="close" type="primary" onClick={() => setEvalModalVisible(false)}>Tancar</Button>]}
                width={620}
            >
                {evalResult && (
                    <div>
                        <div style={{ textAlign: 'center', marginBottom: 16 }}>
                            <Title level={2} style={{ margin: 0, color: evalResult.isCorrect ? '#52c41a' : '#f5222d' }}>
                                {evalResult.score} / 100
                            </Title>
                            <Text type="secondary">
                                {evalResult.penalitzacioTotal && evalResult.penalitzacioTotal > 0 ? (
                                    <span>
                                        Nota base: <strong>{evalResult.notaBase || 0} pts</strong> · Penalització: <strong style={{ color: '#f5222d' }}>-{evalResult.penalitzacioTotal} pts</strong> ({evalResult.intentsFallitsPrevis} intents)
                                    </span>
                                ) : (
                                    "Puntuació obtinguda en base a la rúbrica i tests del script"
                                )}
                            </Text>
                        </div>

                        <div style={{ display: 'flex', justifyContent: 'space-around', background: '#fafafa', padding: 12, borderRadius: 6, marginBottom: 16 }}>
                            <div>
                                <div style={{ fontWeight: 600 }}>Taules</div>
                                <div style={{ color: '#13c2c2' }}>{evalResult.summary.tablesMatched} / {evalResult.summary.tablesExpected}</div>
                            </div>
                            <div>
                                <div style={{ fontWeight: 600 }}>Claus Primàries (PK)</div>
                                <div style={{ color: '#cf1322' }}>{evalResult.summary.pksMatched} / {evalResult.summary.pksExpected}</div>
                            </div>
                            <div>
                                <div style={{ fontWeight: 600 }}>Claus Foranes (FK)</div>
                                <div style={{ color: '#096dd9' }}>{evalResult.summary.fksMatched} / {evalResult.summary.fksExpected}</div>
                            </div>
                        </div>

                        <Title level={5}>Comprovacions:</Title>
                        <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                            {evalResult.feedback.map((f, idx) => (
                                <Alert
                                    key={idx}
                                    type={f.type}
                                    showIcon
                                    message={f.message}
                                    style={{ marginBottom: 6 }}
                                />
                            ))}
                        </div>
                    </div>
                )}
            </Modal>
        </div>
    );
};

export default ScriptRelationalIde;
